import { useEffect, useRef, useState } from 'react'
import { assetCache } from '@/engine/asset-cache'
import type { PlaybackResult } from '@/types/player.types'
import { playerLogger } from '@/utils/player-logger'

interface Props {
  assetId: string
  assetUrl: string
  /** SHA-256 esperado do arquivo original — validado no download antes de cachear/reproduzir. */
  checksum?: string
  durationSeconds: number
  /** `played: false` quando a imagem nunca ficou visível (falha de fonte/checksum/load) — F-07. */
  onEnd: (result: PlaybackResult) => void
}

export function ImageRenderer({ assetId, assetUrl, checksum, durationSeconds, onEnd }: Props) {
  const [src, setSrc] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const endSentRef = useRef(false)
  const onEndRef = useRef(onEnd)
  onEndRef.current = onEnd

  useEffect(() => {
    let cancelled = false
    endSentRef.current = false
    setSrc(null)
    setVisible(false)

    const signalEnd = () => {
      if (cancelled || endSentRef.current) return
      endSentRef.current = true
      onEndRef.current({ played: false })
    }

    assetCache
      .ensureReady(assetId, assetUrl, checksum)
      .then((url) => {
        if (cancelled) return
        setSrc(url)
      })
      .catch((err) => {
        playerLogger.warn('[ImageRenderer] Source resolution failed, advancing', err)
        signalEnd()
      })

    return () => {
      cancelled = true
    }
  }, [assetId, assetUrl, checksum])

  useEffect(() => {
    if (!visible) return

    const startedAt = Date.now()
    timerRef.current = setTimeout(() => {
      if (endSentRef.current) return
      endSentRef.current = true
      // Imagem sempre passa por ensureReady → verificada quando a agenda traz checksum (LOW-8).
      onEndRef.current({
        played: true,
        actualSeconds: (Date.now() - startedAt) / 1000,
        startedAt,
        contentVerified: !!checksum
      })
    }, durationSeconds * 1000)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [visible, durationSeconds, checksum])

  if (!src) return null

  const handleLoad = () => {
    requestAnimationFrame(() => {
      setVisible(true)
    })
  }

  const handleError = () => {
    if (endSentRef.current) return
    endSentRef.current = true
    playerLogger.warn('[ImageRenderer] Image load failed, advancing')
    onEndRef.current({ played: false })
  }

  return (
    <img
      src={src}
      alt=""
      draggable={false}
      onLoad={handleLoad}
      onError={handleError}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.4s ease',
        background: '#000'
      }}
    />
  )
}
